import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore, store} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import campaignsApi from 'common/src/modules/campaigns/campaigns.api';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';

const searchMatchingSignalsToolSchema = z.object({
  searchTerm: z.string().describe('Search term to find matching signals for product targeting'),
});

export default function searchContentSignalsTool(_store: CLStore) {
  async function execute({
    input: {searchTerm},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof searchMatchingSignalsToolSchema,
    ContentCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ContentCardScratchpadState>(
      agentScratchpad,
      NodeType.CONTENT_FEED,
    );

    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolError('Campaign is required to get content signals');
    }

    if (!versionId) {
      return toolError('Version ID is required to get content signals');
    }

    const signals = await campaignsApi.getSignalsByNameForContentCard({
      signalName: searchTerm,
      campaignId: campaign.id,
      versionId,
    });

    const signalsMetadata = signals.map((signal) => ({
      signalKey: signal.value,
      signalName: signal.name,
      category: signal.category,
    }));

    validatedScratchpad.card.availableSignals = [
      ...(validatedScratchpad.card.availableSignals || []),
      ...signalsMetadata,
    ];

    return toolResult(JSON.stringify({signals: validatedScratchpad.card.availableSignals}));
  }

  return tool({
    name: V2CardAgentToolName.SearchContentSignals,
    displayName: 'Searching for content signals',
    description: 'Search for content signals to use in matching conditions',
    inputSchema: searchMatchingSignalsToolSchema,
    execute,
  });
}
